import { useEffect, useRef, useState, type ChangeEvent } from "react";

/**
 * Geboorte-/sterfdatum: opgeslagen als lege string, alleen jaar (JJJJ),
 * jaar-maand (JJJJ-MM), of volledige datum (JJJJ-MM-DD).
 * In het veld tonen we Nederlands: JJJJ, MM-JJJJ, of DD-MM-JJJJ.
 */

/** Volledige DD-MM-JJJJ → JJJJ-MM-DD (alleen bij geldige kalenderdag). */
function parseDdMmYyyyToIso(display: string): string | null {
  const digits = display.replace(/\D/g, "");
  if (digits.length !== 8) return null;
  const d = Number(digits.slice(0, 2));
  const mo = Number(digits.slice(2, 4));
  const y = Number(digits.slice(4, 8));
  if (y < 1000 || y > 9999 || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Invoer (getypt of geplakt) → genormaliseerde opslag, of ongeldig. */
function tryParseToStored(raw: string): { valid: true; stored: string } | { valid: false } {
  const t = raw.trim().replace(/\//g, "-");
  if (t === "") return { valid: true, stored: "" };

  const full = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (full) {
    const y = Number(full[1]);
    const mo = Number(full[2]);
    const d = Number(full[3]);
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return { valid: false };
    return { valid: true, stored: `${full[1]}-${full[2]}-${full[3]}` };
  }

  const ym = t.match(/^(\d{4})-(\d{2})$/);
  if (ym) {
    const y = Number(ym[1]);
    const mo = Number(ym[2]);
    if (y < 1000 || y > 9999 || mo < 1 || mo > 12) return { valid: false };
    return { valid: true, stored: `${ym[1]}-${ym[2]}` };
  }

  const yOnly = t.match(/^(\d{4})$/);
  if (yOnly) {
    const y = Number(yOnly[1]);
    if (y < 1000 || y > 9999) return { valid: false };
    return { valid: true, stored: yOnly[1] };
  }

  const dmy = parseDdMmYyyyToIso(t);
  if (dmy) return { valid: true, stored: dmy };

  const my = t.match(/^(\d{1,2})[-/](\d{4})$/);
  if (my) {
    const mo = Number(my[1]);
    const y = Number(my[2]);
    if (y < 1000 || y > 9999 || mo < 1 || mo > 12) return { valid: false };
    return { valid: true, stored: `${y}-${String(mo).padStart(2, "0")}` };
  }

  return { valid: false };
}

/** Opslag → weergave in het tekstveld. */
function storedToDisplay(stored: string): string {
  const t = (stored || "").trim();
  if (!t) return "";
  const full = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (full) return `${full[3]}-${full[2]}-${full[1]}`;
  const ym = t.match(/^(\d{4})-(\d{2})$/);
  if (ym) return `${ym[2]}-${ym[1]}`;
  const yOnly = t.match(/^(\d{4})$/);
  if (yOnly) return yOnly[1];
  return t;
}

function filterTypable(raw: string): string {
  return raw.replace(/[^\d\-/]/g, "").slice(0, 10);
}

function emitChange(
  onChange: (e: ChangeEvent<HTMLInputElement>) => void,
  name: string,
  value: string,
  inputType: string
) {
  const target = { name, value, type: inputType } as unknown as HTMLInputElement;
  onChange({ target, currentTarget: target } as ChangeEvent<HTMLInputElement>);
}

function CalendarIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

export interface IsoDateInputProps {
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Optioneel: zelfde id als label htmlFor */
  id?: string;
}

/**
 * Geboorte-/sterfdatum: tekstveld (jaar, maand+jaar, of volledige datum) + verborgen
 * datumkiezer voor een concrete dag (JJJJ-MM-DD).
 */
export default function IsoDateInput({ name, value, onChange, id }: IsoDateInputProps) {
  const pickerRef = useRef<HTMLInputElement>(null);
  const [display, setDisplay] = useState(() => storedToDisplay(value));

  useEffect(() => {
    setDisplay(storedToDisplay(value));
  }, [value]);

  function emitStored(stored: string) {
    emitChange(onChange, name, stored, "text");
  }

  function commitDisplay(current: string) {
    const r = tryParseToStored(current);
    if (r.valid) {
      emitStored(r.stored);
      setDisplay(r.stored === "" ? "" : storedToDisplay(r.stored));
    } else {
      setDisplay(storedToDisplay(value));
    }
  }

  function openNativePicker() {
    commitDisplay(display);
    const el = pickerRef.current;
    if (!el) return;
    try {
      el.showPicker?.();
    } catch {
      el.focus();
      el.click();
    }
  }

  const pickerValue = /^\d{4}-\d{2}-\d{2}$/.test((value || "").trim()) ? (value as string).trim() : "";

  return (
    <div className="iso-date-ios-wrap">
      <input
        id={id}
        name={name}
        type="text"
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        placeholder="JJJJ of MM-JJJJ of DD-MM-JJJJ"
        title="Jaar (bijv. 1990), maand en jaar (06-1990 of 1990-06), of volledige datum (21-05-1990). Knop rechts opent de datumkiezer voor een hele dag."
        className="iso-date-ios-text"
        value={display}
        onChange={(e) => {
          const next = filterTypable(e.target.value);
          setDisplay(next);
          const r = tryParseToStored(next);
          if (r.valid) emitStored(r.stored);
        }}
        onBlur={(e) => commitDisplay(e.currentTarget.value)}
      />
      <button
        type="button"
        className="iso-date-ios-calendar-btn"
        aria-label="Datumkiezer openen"
        title="Datumkiezer"
        onMouseDown={(e) => e.preventDefault()}
        onClick={openNativePicker}
      >
        <CalendarIcon />
      </button>
      <input
        ref={pickerRef}
        type="date"
        className="iso-date-ios-picker-proxy"
        value={pickerValue}
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const iso = e.target.value;
          emitStored(iso);
          setDisplay(iso ? storedToDisplay(iso) : "");
        }}
      />
    </div>
  );
}
